import { useState } from "react";
import { 
  Send, 
  Users, 
  BarChart3, 
  Zap, 
  Shield, 
  Globe,
  ChevronRight,
  Check,
  Star,
  ArrowRight,
  Sparkles,
  Target,
  MessageSquare,
  Bot,
  Rocket
} from "lucide-react";

const LandingPage = () => {
  const [email, setEmail] = useState("");

  const features = [
    {
      icon: Users,
      title: "Monitor Plus",
      titleAr: "مونيتور بلس",
      description: "Collect thousands of targeted members from any Telegram group automatically",
      descriptionAr: "اجمع آلاف الأعضاء المستهدفين من أي مجموعة تليجرام تلقائياً",
      color: "#00F0FF"
    },
    {
      icon: Send,
      title: "Mega-Reachy",
      titleAr: "ميجا ريتشي",
      description: "Send smart bulk messages with A/B testing and detailed analytics",
      descriptionAr: "أرسل رسائل جماعية ذكية مع اختبار A/B وتحليلات مفصلة",
      color: "#10B981"
    },
    {
      icon: Bot,
      title: "AI Classification",
      titleAr: "تصنيف بالذكاء الاصطناعي",
      description: "Automatically classify members by interests using advanced AI",
      descriptionAr: "صنّف الأعضاء تلقائياً حسب اهتماماتهم باستخدام الذكاء الاصطناعي",
      color: "#7C3AED"
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      titleAr: "تحليلات متقدمة",
      description: "Track campaign performance with real-time detailed reports",
      descriptionAr: "تابع أداء حملاتك بتقارير تفصيلية لحظة بلحظة",
      color: "#F59E0B"
    },
    {
      icon: Shield,
      title: "100% Secure",
      titleAr: "آمن 100%",
      description: "Your data is encrypted and protected with highest security standards",
      descriptionAr: "بياناتك مشفرة ومحمية بأعلى معايير الأمان",
      color: "#EF4444"
    },
    {
      icon: Globe,
      title: "Full Arabic Support",
      titleAr: "دعم عربي كامل",
      description: "Complete Arabic interface with RTL support and Arabic customer service",
      descriptionAr: "واجهة عربية كاملة مع دعم فني بالعربي",
      color: "#EC4899"
    }
  ];

  const plans = [
    {
      name: "Free",
      nameAr: "مجاني",
      price: "$0",
      period: "/week",
      periodAr: "/أسبوع",
      features: ["100 members", "1 group", "JSON export", "Email support"],
      featuresAr: ["100 عضو", "مجموعة واحدة", "تصدير JSON", "دعم بالبريد"],
      popular: false
    },
    {
      name: "Standard",
      nameAr: "قياسية",
      price: "$24.99",
      period: "/month",
      periodAr: "/شهر",
      features: ["100,000 members", "Unlimited groups", "AI classification", "Priority support", "Mega-Reachy FREE"],
      featuresAr: ["100,000 عضو", "مجموعات غير محدودة", "تصنيف AI", "دعم أولوية", "Mega-Reachy مجاني"],
      popular: true
    },
    {
      name: "Enterprise",
      nameAr: "مؤسسي",
      price: "$149",
      period: "/month",
      periodAr: "/شهر",
      features: ["1,000,000 members", "API access", "White label", "Dedicated manager", "Custom features"],
      featuresAr: ["1,000,000 عضو", "وصول API", "باسم شركتك", "مدير مخصص", "مميزات خاصة"],
      popular: false
    }
  ];

  const testimonials = [
    {
      name: "أحمد محمد",
      role: "صاحب متجر إلكتروني",
      content: "oeia.io غيّرت طريقة تسويقي للأبد! زادت مبيعاتي 280% في شهر واحد",
      rating: 5
    },
    {
      name: "سارة أحمد",
      role: "مسوقة رقمية",
      content: "أفضل منصة عربية للتسويق عبر تليجرام. سهلة جداً وفعالة",
      rating: 5
    },
    {
      name: "خالد العتيبي",
      role: "مدرب ومحاضر",
      content: "باستخدام Mega-Reachy، امتلأت دوراتي بالمتدربين. شكراً oeia.io!",
      rating: 5
    }
  ];

  const stats = [
    { value: "10,000+", label: "Active Users", labelAr: "مستخدم نشط" },
    { value: "50M+", label: "Messages Sent", labelAr: "رسالة مُرسلة" },
    { value: "98%", label: "Satisfaction Rate", labelAr: "نسبة الرضا" },
    { value: "24/7", label: "Support", labelAr: "دعم فني" }
  ];

  return (
    <div className="min-h-screen bg-black text-white" dir="rtl">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-32">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8">
              <Sparkles size={16} className="text-cyan-400" />
              <span className="text-sm text-neutral-300">منصة التسويق الذكي #1 بالعربي</span>
            </div>
            
            {/* Logo & Title */}
            <h1 className="text-5xl sm:text-7xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
                oeia.io
              </span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-neutral-300 mb-4">
              <span className="text-cyan-400 font-semibold">Monitor Plus</span>
              {" "}+{" "}
              <span className="text-emerald-400 font-semibold">Mega-Reachy</span>
            </p>
            
            <p className="text-lg sm:text-xl text-neutral-400 max-w-2xl mx-auto mb-8">
              اجمع آلاف العملاء المحتملين وأرسل لهم رسائل ذكية ومخصصة
              <br />
              كل ذلك من منصة واحدة متكاملة!
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-xl font-bold text-lg text-black hover:opacity-90 transition-all flex items-center gap-2">
                ابدأ مجاناً الآن
                <ArrowRight size={20} className="group-hover:-translate-x-1 transition-transform rotate-180" />
              </button>
              <button className="px-8 py-4 border border-white/20 rounded-xl font-semibold hover:bg-white/5 transition-all">
                شاهد العرض التوضيحي
              </button>
            </div>
            
            {/* Trust Badge */}
            <div className="mt-12 flex items-center justify-center gap-8 text-neutral-500">
              <div className="flex items-center gap-2">
                <Shield size={20} className="text-emerald-500" />
                <span>آمن 100%</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap size={20} className="text-amber-500" />
                <span>أسبوع مجاني</span>
              </div>
              <div className="flex items-center gap-2">
                <Check size={20} className="text-cyan-500" />
                <span>بدون بطاقة ائتمان</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
                  {stat.value}
                </div>
                <div className="text-neutral-400 mt-2">{stat.labelAr}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              كل ما تحتاجه في{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
                منصة واحدة
              </span>
            </h2>
            <p className="text-neutral-400 max-w-2xl mx-auto">
              أدوات قوية ومتكاملة لجمع العملاء المحتملين والتواصل معهم بذكاء
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-all group"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${feature.color}20` }}
                >
                  <feature.icon size={28} style={{ color: feature.color }} />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.titleAr}</h3>
                <p className="text-neutral-400">{feature.descriptionAr}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              كيف تعمل المنصة؟
            </h2>
            <p className="text-neutral-400">
              3 خطوات بسيطة للوصول لآلاف العملاء
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                icon: Target,
                title: "اجمع",
                description: "أضف رابط أي مجموعة تليجرام وابدأ بجمع الأعضاء المهتمين تلقائياً"
              },
              {
                step: "2",
                icon: Bot,
                title: "صنّف",
                description: "الذكاء الاصطناعي يصنف الأعضاء حسب اهتماماتهم تلقائياً"
              },
              {
                step: "3",
                icon: Rocket,
                title: "أرسل",
                description: "استخدم Mega-Reachy لإرسال رسائل مخصصة وتابع النتائج"
              }
            ].map((item, index) => (
              <div key={index} className="relative text-center">
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 flex items-center justify-center mb-6">
                  <item.icon size={36} className="text-black" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{item.title}</h3>
                <p className="text-neutral-400">{item.description}</p>
                
                {index < 2 && (
                  <div className="hidden md:block absolute top-10 left-0 w-full">
                    <ChevronRight size={24} className="text-neutral-600 rotate-180" style={{ marginLeft: '-50%' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              خطط الأسعار
            </h2>
            <p className="text-neutral-400">
              اختر الخطة المناسبة لاحتياجاتك
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`relative p-8 rounded-2xl ${
                  plan.popular 
                    ? 'bg-gradient-to-b from-cyan-500/20 to-emerald-500/20 border-2 border-cyan-500/50' 
                    : 'bg-white/5 border border-white/10'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full text-black text-sm font-bold">
                    الأكثر شيوعاً
                  </div>
                )}
                
                <h3 className="text-2xl font-bold mb-2">{plan.nameAr}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-neutral-400">{plan.periodAr}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.featuresAr.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2">
                      <Check size={18} className="text-emerald-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button className={`w-full py-3 rounded-xl font-bold transition-all ${
                  plan.popular
                    ? 'bg-gradient-to-r from-cyan-500 to-emerald-500 text-black hover:opacity-90'
                    : 'border border-white/20 hover:bg-white/5'
                }`}>
                  ابدأ الآن
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              ماذا يقول عملاؤنا؟
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-6 rounded-2xl bg-black border border-white/10">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-neutral-300 mb-4">"{testimonial.content}"</p>
                <div>
                  <div className="font-bold">{testimonial.name}</div>
                  <div className="text-sm text-neutral-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            جاهز للبدء؟
          </h2>
          <p className="text-neutral-400 mb-8">
            انضم لأكثر من 10,000 مسوق يستخدمون oeia.io
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="بريدك الإلكتروني"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500 outline-none"
            />
            <button className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-xl font-bold text-black hover:opacity-90 transition-all">
              ابدأ مجاناً
            </button>
          </div>
          
          <p className="text-sm text-neutral-500 mt-4">
            أسبوع مجاني • بدون بطاقة ائتمان • الغِ في أي وقت
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
              oeia.io
            </div>
            <div className="text-neutral-500 text-sm">
              © 2024 oeia.io - جميع الحقوق محفوظة
            </div>
            <div className="flex gap-6 text-neutral-400">
              <a href="#" className="hover:text-white transition-colors">سياسة الخصوصية</a>
              <a href="#" className="hover:text-white transition-colors">الشروط والأحكام</a>
              <a href="#" className="hover:text-white transition-colors">تواصل معنا</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
